import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { Spinner, Alert, EmptyState, SortHeader, nextSort, sortRows } from '../../components/UI';

// Colour the average-score pill the same way the user result rings are coloured.
function scoreTone(pct) {
  if (pct == null) return 'muted';
  if (pct >= 75) return 'success';
  if (pct >= 50) return 'warning';
  return 'danger';
}

// Sorts after every real value, so blank team lead / head quarter sink to the
// bottom in ascending order instead of leading it.
const BLANK_LAST = '\uffff';

// How each sortable column pulls its comparable value out of a user row.
const SORT_ACCESSORS = {
  employee_id: u => (u.employee_id || '').toLowerCase(),
  team_lead: u => (u.team_lead || BLANK_LAST).toLowerCase(),
  head_quarter: u => (u.head_quarter || BLANK_LAST).toLowerCase(),
};

export default function UserActivity() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: null, dir: 'asc' }); // null = server order (most attempts first)

  const onSort = col => setSort(s => nextSort(s, col));

  useEffect(() => {
    api.get('/reports/user-activity').then(setRows).catch(e => setError(e.message));
  }, []);

  if (!rows && !error) return <Spinner />;

  const term = q.trim().toLowerCase();
  const list = sortRows((rows || []).filter(r =>
    !term || r.name.toLowerCase().includes(term) || (r.mobile || '').includes(term) ||
    (r.employee_id || '').toLowerCase().includes(term)
  ), sort, SORT_ACCESSORS);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="eyebrow">Administration</div>
          <h1>User Activity</h1>
        </div>
        {rows && rows.length > 0 && (
          <input
            type="search"
            className="input-search"
            placeholder="Search name, employee ID or mobile…"
            value={q}
            onChange={e => setQ(e.target.value)}
            style={{ maxWidth: 300 }}
          />
        )}
      </div>

      <Alert type="danger">{error}</Alert>

      {rows && rows.length === 0 && !error && (
        <div className="card"><EmptyState title="No users yet" /></div>
      )}

      {rows && rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <SortHeader label="Employee ID" col="employee_id" sort={sort} onSort={onSort} />
                <th>Name</th>
                <th>Mobile</th>
                <SortHeader label="Team lead" col="team_lead" sort={sort} onSort={onSort} />
                <SortHeader label="Head quarter" col="head_quarter" sort={sort} onSort={onSort} />
                <th>Exams attempted</th>
                <th>Average score</th>
              </tr>
            </thead>
            <tbody>
              {list.map(u => {
                const pct = u.avg_pct == null ? null : Number(u.avg_pct);
                return (
                  <tr key={u.id}>
                    <td className="mono">{u.employee_id}</td>
                    <td>{u.name}</td>
                    <td className="mono">{u.mobile}</td>
                    <td>{u.team_lead || <span className="text-muted">&mdash;</span>}</td>
                    <td>{u.head_quarter || <span className="text-muted">&mdash;</span>}</td>
                    <td>
                      {u.exams_attempted > 0 ? (
                        <Link to={`/admin/user-activity/${u.id}`} className="link-count">
                          <span className="link-count-num">{u.exams_attempted}</span>
                          <svg className="link-count-arrow" width="18" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="13 6 19 12 13 18" />
                          </svg>
                        </Link>
                      ) : (
                        <span className="text-muted">0</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${scoreTone(pct)}`}>
                        {pct == null ? '—' : `${pct}%`}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr><td colSpan={7} className="text-sm text-muted">No users match “{q}”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
